import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBusinessAuthContext } from './useBusinessAuthContext';
import BusinessMenu from './BusinessMenu';
import Sidebar from "../../components/Sidebar";
import API_URL from '../../api/API';

const BusinessEditProfile = () => {
  const { business, dispatch } = useBusinessAuthContext();
  const navigate = useNavigate();

  const [businessTypes, setBusinessTypes] = useState([]);
  const [businessName, setBusinessName] = useState('');
  
  const [canEditBusinessType, setCanEditBusinessType] = useState(false);
  const [businessType, setBusinessType] = useState('');

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!business) return;

    setBusinessName(business.businessName || '');
    setBusinessType(business.businessType ? business.businessType.id : ''); // 👈 use .id
    setFullName(business.fullName || '');
    setEmail(business.email || '');
    setPhone(business.phone || '');
    setCanEditBusinessType(!!business.canEditBusinessType);

    // Fetch master business types
    const fetchBusinessTypes = async () => {
      try {
        const res = await fetch(API_URL.MASTER_BUSINESS_TYPE);
        const data = await res.json();
        if (data.success) {
          setBusinessTypes(data.data); // ✅ [{ _id, name }]
        }
      } catch (err) {
        console.error('Error fetching business types:', err);
      }
    };

    fetchBusinessTypes();
  }, [business]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMsg('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL.BUSINESS_EDIT_PROFILE}${business.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${business.token}`,
          "x-session-id": business.sessionId, 
          "x-frontend-url": window.location.pathname   
        },
        body: JSON.stringify({
          businessName,
          businessType, // ✅ always ID
          fullName,
          phone,
        }),
      });

      const data = await res.json();
      setLoading(false);

      if (data.success) {
        const updated = {
          ...data.business,
          token: business.token,
        };

        dispatch({
          type: 'BUSINESS_LOGIN',
          payload: updated,
        });

        localStorage.setItem('business', JSON.stringify(updated));

        // ✅ update form fields
        setBusinessName(updated.businessName || '');
        setBusinessType(updated.businessType.id); // 👈 use .id
        setFullName(updated.fullName || '');
        setPhone(updated.phone || '');

        setMsg('Profile updated successfully.');
      } else {
        setError(data.message || 'Update failed.');
      }
    } catch (err) {
      console.error('Error updating profile:', err);
      setLoading(false);
      setError('Server error during profile update.');
    }
  };

  return (
    <div className="h-screen flex bg-background">
      <Sidebar />

      <div className="flex-1 overflow-auto py-10">
        <div className="max-w-3xl mx-auto px-6">

          {/* Back
          <button
            onClick={() => navigate("/business/dashboard")}
            className="text-sm text-muted-foreground mb-6"
          >
            ← Back
          </button>  */}

          {/* Header */}
          <h1 className="text-2xl font-semibold mb-1">
            Edit Profile
          </h1>
          <p className="text-sm text-muted-foreground mb-8">
            Update your business details and contact information
          </p>

          {/* Card */}
          <div className="bg-white rounded-xl shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">

              {/* Business Name */}
              <div>
                <label className="block mb-1 text-foreground">
                  Business Name
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  className="w-full rounded-14 bg-[#F7F7F5] outline-none px-4 py-3"
                  required
                />
              </div>

              {/* Business Type */}
              <div>
                <label className="block mb-1 text-foreground">
                  Business Type
                </label>
                <select
                  value={businessType}
                  onChange={(e) => setBusinessType(e.target.value)}
                  disabled={!canEditBusinessType}
                  className={`w-full rounded-14 bg-[#F7F7F5] outline-none px-4 py-3 ${
                    !canEditBusinessType
                      ? "text-gray-400 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <option value="">Select your industry</option>
                  {businessTypes.map((type) => (
                    <option key={type._id} value={type._id}>
                      {type.name}
                    </option>
                  ))}
                </select>

                {!canEditBusinessType && (
                  <p className="text-xs text-muted-foreground mt-1">
                    Business type cannot be changed after setup
                  </p>
                )}
              </div>

              {/* Full Name */}
              <div>
                <label className="block mb-1 text-foreground">
                  Full Name
                </label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-14 bg-[#F7F7F5] outline-none px-4 py-3"
                  required
                />
              </div>

              {/* Email */}
              <div>
                <label className="block mb-1 text-foreground">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  disabled
                  className="w-full rounded-14 bg-gray-100 text-gray-400 px-4 py-3"
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block mb-1 text-foreground">
                  Phone
                </label>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-14 bg-[#F7F7F5] outline-none px-4 py-3"
                />
              </div>

              {/* Feedback */}
              {error && (
                <p className="text-sm text-red-500">{error}</p>
              )}
              {msg && (
                <p className="text-sm text-green-600">{msg}</p>
              )}

              {/* CTA */}
              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-6 py-3 rounded-full font-medium flex items-center gap-2 ${
                    !loading
                      ? "bg-yellow-500 hover:bg-yellow-600 text-black"
                      : "bg-yellow-100 text-yellow-400 cursor-not-allowed"
                  }`}
                >
                  {loading ? "Saving..." : "Save Changes →"}
                </button>
              </div>

            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessEditProfile;
